import { CSSProperties, ReactNode, Key } from "react";
import { IObject, IResponse } from "../constants/interface";

export interface SelectTabProps extends SelectTabDropdownBaseProps, SelectTabInputBaseProps, SelectTabTagsBaseProps {
  /** 样式 */
  style?: CSSProperties,
  /** 类名 */
  className?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 指定默认选中的条目 */
  defaultValue?: SelectTabValue;
  /** 额外参数 */
  otherParams?: IObject;
  /** 下拉选项的值对应data的那个字段 */
  dataKey?: string | false;
  /** 宽度 */
  width?: number;
  /** 下拉菜单和选择器同宽。默认将设置 min-width，当值小于选择框宽度时会被忽略 */
  dropdownMatchSelectWidth?: number;
  /** 下拉列表标识 用于缓存 可能造成数据更新不及时 */
  selectTabKey?: string;
  /** 禁用时超出字符串是否省略 */
  isDisabledEllipsis?: boolean;
  /** 选项、接口、参数改变是否清空当前选中值 */
  changeEmptyValue?: boolean;
  /** 禁用时展示文字 */
  disabledRender?: ReactNode;
  /** 本地搜索参数 */
  localSearchParams?: IObject;
  /** 开启缓存是接口是否可传参数 */
  isCacheHasParams?: boolean;
  /** 不进行本地搜索的字段*/
  localSearchExcludeFieldname?: string | string[];
  /** 数据加载时机 */
  loadingType?: LoadingType;
  /** 远程数据加载完成回调 */
  onFetchcomplete?: (data: any[]) => any[];
  /** 过滤、处理选项数据 */
  formatOptions?: (options?: SelectTabOption[]) => SelectTabOption[] | undefined;
  /** 接口API */
  fetchApi?: (params: any) => Promise<IResponse>;
  /** 失去焦点时回调 */
  onBlur?: () => void;
  /** 获得焦点时回调 */
  onFocus?: () => void;
  /** 选中 option，或 input 的 value 变化时，调用此函数 */
  onChange?: (value?: SelectTabValue, options?: SelectTabOption[]) => void;
  /** 被选中时调用，参数为选中项的 value (或 key) 值 */ 
  onSelect?: (value?: SelectTabValue, options?: SelectTabOption[]) => void;
  /** 请求接口之前回调，如果返回false则不发起本次请求 */
  onBefore?: (params: any) => any;
};
export interface SelectTabDropdownBaseProps {
  /** 值 */
  value?: SelectTabValue;
  /** 搜索字段 默认为label对应的字段名 */
  searchKey?: string;
  /** 样式 */
  dropdownStyle?: CSSProperties;
  /** 类名 */
  dropdownClassName?: string;
  /** 下拉层级 */
  zIndex?: number;
  /** 支持清除 */
  allowClear?: boolean;
  /** 选项 */
  options?: SelectTabOption[];
  /** 默认选项 */
  initOptions?: SelectTabOption[];
  /** tabs选项配置 */
  tabItems?: SelectTabItem[];
  /** 自定义节点 label、subLabel、value、icon 的字段 */
  fieldNames?: SelectTabFieldNamesType;
  /** 是否为本地搜索 */
  isLocalSearch?: boolean;
  /** 搜索框提示 */
  searchPlaceholder?: string;
  /** 是否显示搜索框 */
  showSearch?: boolean;
  /** 是否多选 */
  isMultiple?: boolean;
  /** 列的数量 */
  columnNumber?: number;
  /** 设置弹窗滚动高度 */
  listHeight?: number;
  /** 是否开启键盘事件 */
  isKeyboardEvent?: boolean;
  /** 选项为空时的提示内容 */
  optionsEmptyTip?: ReactNode;
  /** 搜索选项为空时的提示内容 */
  searchOptionsEmptyTip?: ReactNode;
  /** 本地搜索时是否区分大小写 */
  searchDiscriminateCase?: boolean;
  /** 是否需要全选按钮，只有在多选的时候才生效 */
  isSelectAll?: boolean;
  /** 是否需要反选按钮，只有在多选的时候才生效 */
  isReverseSelection?: boolean;
  /** 子类分组是否需要全选按钮，只有在多选的时候才生效 */
  isGroupSelectAll?: boolean;
  /** 子类分组是否需要反选按钮，只有在多选的时候才生效 */
  isGroupReverseSelection?: boolean;
  /** 是否显示tab栏 */
  showTab?: boolean;
  /** 菜单渲染父节点。默认渲染到 body 上 */
  getPopupContainer?: () => HTMLElement | HTMLElement;
  /** 清除内容时回调 */
  onClear?: () => void;
  /** 排序方法 */
  sort?: (a: SelectTabInOption, b: SelectTabInOption) => number;
  /** tabs切换时选项处理 */
  tabsChangeFormatOption?: (options?: SelectTabOption[], tabKey?: string) => SelectTabOption[];
}
export interface SelectTabInputBaseProps {
  /** 值 */
  value?: SelectTabValue;
  /** 值为空时提示 */
  placeholder?: string;
  /** 是否处于加载中状态 */
  loading?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 支持清除 */
  allowClear?: boolean;
  /** 清除内容时回调 */
  onClear?: () => void;
}
export interface SelectTabTagsBaseProps {
  /** 值 */
  value?: SelectTabValue;
  /** 选项 */
  options?: SelectTabOption[];
  /** 自定义节点 label、subLabel、value、icon 的字段 */
  fieldNames?: SelectTabFieldNamesType;
  /** 是否禁用 */
  disabled?: boolean;
}
export interface SelectTabOption {
  /** 标题 */
  label?: ReactNode;
  /** 值 */
  value?: string;
  /** 图标 */
  icon?: ReactNode;
  /** 副标题 */
  subLabel?: ReactNode;
  /** 子节点 目前外部传入数据不支持子节点 */
  children?: SelectTabOption[];
  /** 其他 */
  [prop: string]: any;
}
export interface SelectTabInOption {
  /** 标题 */
  label?: ReactNode;
  /** 值 */
  value?: string;
  /** 图标 */
  icon?: ReactNode;
  /** 副标题 */
  subLabel?: ReactNode;
  /** 子节点 目前外部传入数据不支持子节点 */
  children?: SelectTabOption[];
}
export interface SelectTabItem {
  /** 标题 */
  label: ReactNode;
  /** 值 */
  value: string;
  /** 图标 */
  icon?: ReactNode;
  /** value或label对应的字典 */
  enumCode?: string;
  /** 分组值对应标题字段 */
  groupLabel?: string;
  /** 分组值对应副标题字段 */
  groupSubLabel?: string;
  /** 分组值对应图标字段 */
  groupIcon?: string;
  /** 是否隐藏子节点的图标 */
  hideChildrenIcon?: boolean;
}
export interface SelectTabFieldNames {
  /** 标题 */
  label: string;
  /** 副标题 */
  subLabel: string;
  /** 图标 */
  icon: string;
  /** 值 */
  value: string;
}
export type SelectTabFieldNamesType = Partial<SelectTabFieldNames>;
/** fieldNames默认值 */
export const DEFAULT_FIELD_NAMES: SelectTabFieldNames = {
  label: 'label',
  subLabel: 'subLabel',
  icon: 'icon',
  value: 'value',
};
export interface DropdownOffset {
  /** 距离页面左部的距离 */
  left: number;
  /** 距离页面头部的距离 */
  top: number;
  /** 距离页面底部的距离 */
  bottom?: number;
}
export type SelectTabValue = Key | Key[];
/** 未匹配的数据分组 */
export const OTHER_TAB_KEY: string = '其他';
/** 数据加载时机  初始化 | 打开弹窗 */
export type LoadingType = 'init' | 'open';
